import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { shortOrderRef } from "@nexus/contracts";
import {
  Badge,
  Button,
  Separator,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  getAvatarColor,
} from "@nexus/ui";
import { getVendorInitials } from "../../lib/vendorInitials";
import {
  formatVendorPriceFrom,
  type VendorCatalogCardModel,
} from "../../lib/vendor-catalog-model";
import { getFulfillmentStatusLabel } from "../../lib/fulfillment-status-label";
import { AdjustGuidelinesDrawer } from "../AdjustGuidelinesDrawer";
import { DisconnectDialog } from "../DisconnectDialog";
import { ReSignSheet } from "../ReSignSheet";
import { connectionNeedsResign } from "../../lib/terms-compliance";
import type { MarketplaceOrder } from "../../types/marketplace.types";

interface VendorDetailDrawerProps {
  card: VendorCatalogCardModel | null;
  tenantId: string;
  recentOrders: MarketplaceOrder[];
  onClose: () => void;
  onConnect: (vendorId: string) => void;
  onSendScripts: (vendorId: string) => void;
}

export function VendorDetailDrawer({
  card,
  tenantId,
  recentOrders,
  onClose,
  onConnect,
  onSendScripts,
}: VendorDetailDrawerProps) {
  const { t } = useTranslation();
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [resignOpen, setResignOpen] = useState(false);

  const vendor = card?.vendor;
  const connection = card?.connection;
  const paused = card?.availability === "paused";
  const unavailable = card?.availability === "unavailable";
  const needsResign = Boolean(
    connection && !paused && connectionNeedsResign(connection),
  );

  return (
    <>
      <Sheet open={card != null} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="flex flex-col">
          {card && vendor && (
            <>
              <SheetHeader className="pr-12">
                <div className="flex items-start gap-3">
                  {vendor.logoUrl ? (
                    <img
                      src={vendor.logoUrl}
                      alt=""
                      className="size-12 shrink-0 rounded-md border border-border object-contain p-1"
                    />
                  ) : (
                    <span
                      aria-hidden
                      style={{ backgroundColor: getAvatarColor(vendor.name) }}
                      className="flex size-12 shrink-0 items-center justify-center rounded-md text-sm font-semibold text-foreground/80"
                    >
                      {getVendorInitials(vendor.name)}
                    </span>
                  )}
                  <div className="min-w-0">
                    <SheetTitle className="text-[15px] font-semibold">
                      {vendor.name}
                    </SheetTitle>
                    <SheetDescription>
                      {card.positioning ??
                        t(
                          "marketplace.vendors.detail.noPositioning",
                          "Production partner",
                        )}
                    </SheetDescription>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {card.isConnected && (
                        <Badge variant="status-connected">
                          {t("marketplace.catalog.connected", "Connected")}
                        </Badge>
                      )}
                      {paused && (
                        <Badge variant="secondary">
                          {t(
                            "marketplace.vendors.availability.paused",
                            "Paused",
                          )}
                        </Badge>
                      )}
                      {unavailable && (
                        <Badge variant="secondary">
                          {t(
                            "marketplace.vendors.availability.unavailable",
                            "Unavailable",
                          )}
                        </Badge>
                      )}
                      {needsResign && (
                        <Badge variant="outline">
                          {t("marketplace.resign.badge", "Re-sign required")}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <SheetBody className="flex flex-col gap-6">
                <section className="grid grid-cols-3 gap-3 text-sm">
                  <Metric
                    label={t("marketplace.vendors.card.priceFrom", "From")}
                    value={
                      card.priceFrom != null
                        ? formatVendorPriceFrom(card.priceFrom, card.currency)
                        : t(
                            "marketplace.vendors.card.priceOnRequest",
                            "On request",
                          )
                    }
                  />
                  <Metric
                    label={t(
                      "marketplace.vendors.card.turnaround",
                      "Turnaround",
                    )}
                    value={
                      card.turnaroundDays != null
                        ? t("marketplace.vendors.card.turnaroundDays", {
                            count: card.turnaroundDays,
                            defaultValue_one: "{{count}} day",
                            defaultValue_other: "{{count}} days",
                          })
                        : t("marketplace.vendors.card.turnaroundUnknown", "—")
                    }
                  />
                  <Metric
                    label={t("marketplace.vendors.card.onTime", "On-time")}
                    value={
                      card.metrics.onTimeRate != null
                        ? t("marketplace.vendors.card.onTimeValue", {
                            value: Math.round(card.metrics.onTimeRate * 100),
                            defaultValue: "{{value}}%",
                          })
                        : t("marketplace.vendors.card.metricPending", "New")
                    }
                  />
                </section>

                {card.capabilities.length > 0 && (
                  <section>
                    <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t(
                        "marketplace.vendors.detail.capabilities",
                        "Capabilities",
                      )}
                    </h3>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {card.capabilities.map((capability) => (
                        <li
                          key={capability}
                          className="rounded-sm bg-muted px-2 py-1 text-xs font-medium text-foreground"
                        >
                          {t(
                            `marketplace.vendors.capabilities.${capability}`,
                            capability,
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {vendor.packages.filter((pkg) => pkg.isActive).length > 0 && (
                  <section>
                    <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t("marketplace.vendors.detail.packages", "Packages")}
                    </h3>
                    <ul className="mt-2 flex flex-col gap-2">
                      {vendor.packages
                        .filter((pkg) => pkg.isActive)
                        .map((pkg) => (
                          <li
                            key={pkg.id}
                            className="flex items-start justify-between gap-3 rounded-md border border-border px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground">
                                {pkg.name}
                              </p>
                              {pkg.description && (
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                  {pkg.description}
                                </p>
                              )}
                            </div>
                            {pkg.price != null && (
                              <p className="shrink-0 text-sm font-semibold tabular-nums">
                                {formatVendorPriceFrom(pkg.price, pkg.currency)}
                              </p>
                            )}
                          </li>
                        ))}
                    </ul>
                  </section>
                )}

                {connection && (
                  <section>
                    <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t("marketplace.vendors.detail.management", "Connection")}
                    </h3>
                    <dl className="mt-2 grid gap-2 text-sm">
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">
                          {t("marketplace.vendors.detail.package", "Package")}
                        </dt>
                        <dd className="font-medium">
                          {connection.package?.name ??
                            t("marketplace.vendors.detail.noPackage", "None")}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">
                          {t(
                            "marketplace.vendors.detail.connectedAt",
                            "Connected",
                          )}
                        </dt>
                        <dd className="font-medium tabular-nums">
                          {new Date(connection.connectedAt).toLocaleDateString(
                            undefined,
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            },
                          )}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-3">
                        <dt className="text-muted-foreground">
                          {t(
                            "marketplace.vendors.detail.ordersCount",
                            "Orders",
                          )}
                        </dt>
                        <dd className="font-medium tabular-nums">
                          {card.metrics.orderCount}
                        </dd>
                      </div>
                    </dl>
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t(
                        "marketplace.vendors.detail.packageChangeHint",
                        "To change package, disconnect and connect again with a different package.",
                      )}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {needsResign && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setResignOpen(true)}
                        >
                          {t("marketplace.resign.action", "Re-sign terms")}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={paused}
                        onClick={() =>
                          needsResign
                            ? setResignOpen(true)
                            : setAdjustOpen(true)
                        }
                      >
                        {t(
                          "marketplace.connections.adjustGuidelines",
                          "Adjust Guidelines",
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDisconnectOpen(true)}
                      >
                        {t("marketplace.connections.disconnect", "Disconnect")}
                      </Button>
                    </div>
                  </section>
                )}

                <section>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(
                      "marketplace.vendors.detail.recentOrders",
                      "Recent orders",
                    )}
                  </h3>
                  {recentOrders.length === 0 ? (
                    <p className="mt-2 text-sm text-muted-foreground">
                      {t(
                        "marketplace.vendors.detail.noOrders",
                        "No orders with this partner yet.",
                      )}
                    </p>
                  ) : (
                    <ul className="mt-2 flex flex-col gap-2">
                      {recentOrders.slice(0, 5).map((order) => (
                        <li key={order.id}>
                          <Link
                            to="/marketplace"
                            search={{
                              tab: "in-production",
                              order: order.id,
                              vendor: undefined,
                            }}
                            className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm no-underline hover:bg-muted/50"
                          >
                            <span className="font-mono text-xs text-muted-foreground">
                              {shortOrderRef(order.id) ?? order.id}
                            </span>
                            <span className="text-xs text-foreground">
                              {getFulfillmentStatusLabel(
                                t,
                                order.fulfillmentStatus,
                                "client",
                              )}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {vendor.websiteUrl && (
                  <>
                    <Separator />
                    <a
                      href={vendor.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-medium text-muted-foreground hover:text-foreground"
                    >
                      {t("marketplace.catalog.visitWebsite", "Visit website")}
                    </a>
                  </>
                )}
              </SheetBody>

              <SheetFooter>
                {card.isConnected ? (
                  <Button
                    type="button"
                    className="cursor-pointer"
                    disabled={paused}
                    onClick={() => onSendScripts(vendor.id)}
                  >
                    {t(
                      "marketplace.vendors.actions.sendScripts",
                      "Send scripts",
                    )}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="cursor-pointer"
                    disabled={paused || unavailable}
                    onClick={() => onConnect(vendor.id)}
                  >
                    {t("marketplace.catalog.connect", "Connect")}
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {connection && (
        <>
          <AdjustGuidelinesDrawer
            connection={connection}
            tenantId={tenantId}
            open={adjustOpen}
            onClose={() => setAdjustOpen(false)}
            onResignRequired={() => {
              setAdjustOpen(false);
              setResignOpen(true);
            }}
          />
          <ReSignSheet
            connectionId={connection.id}
            vendorId={connection.vendorId}
            tenantId={tenantId}
            open={resignOpen}
            onClose={() => setResignOpen(false)}
            onSuccess={() => setResignOpen(false)}
          />
          <DisconnectDialog
            connectionId={connection.id}
            vendorName={connection.vendor.name}
            tenantId={tenantId}
            open={disconnectOpen}
            onClose={() => setDisconnectOpen(false)}
          />
        </>
      )}
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
        {value}
      </p>
    </div>
  );
}
